import { createLogger } from 'shared/services/logger';

const logger = createLogger('uScope:lib:Renderer');

/**
 * Render controller object. Used to synchronize paint events.
 * Not sure how comfortable I am calling this a renderer, but it controls the render cycle, so let's roll with it.
 * @param viewport
 * @constructor
 */
export function Renderer (viewport) {
  // Store off a ref to the viewport div
  const $viewportDiv = viewport.vpDivGet();
  // This "sizer div" is used to size the viewport. This allows child layers to size themselves to 100% which precludes
  // us from having to make sure they're all in sync as layers are added and removed. We also only have to manually
  // size the one div, instead of n divs. Ideally the viewport div would be sized directly without the need for the
  // sizer div but lots of legacy code depends on it being where it is at the size it is.
  const sizerDiv = document.createElement('div');
  sizerDiv.className = 'us-viewport-sizer';
  $viewportDiv.append(sizerDiv);
  // "Layers" are actually callback items; not sure if "layer" is the right name here.
  const layers = [];

  // Pre-allocate vars to reduce effect of garbage collection.
  let zoom;
  let width;
  let height;
  let left;
  let top;
  let ix;
  let layer;

  // Public methods
  Object.assign(this, {
    /**
         * Adds a "layer" to the layers collection.
         * @param layer (object)
         * layer.container: An HTML element that represents a some aspect of the viewport's image. Note that this class
         *                  will resize / reposition these containers, so don't attempt to do so outside this class.
         *                  Used for: Background image, tiles, and annotation layer
         * layer.updatePosition: Called whenever viewport's state changes. Passes in the new dimensions of the container
         *                       Used for: Updating annotation layer state (TODO: Deprecate if possible).
         * layer.paint: Called whenever viewport's state changes. Passes in the current state and an array of the
         *              names of properties that have changed.
         *              Used for: Painting background image, tiles, and annotations.
         * layer.zoomChange: Called whenever viewport's zoom changes. Passes the current state to the callback.
         *                   Used for: Zoom sliders, annotation repaints, thumbnail updates.
         * layer.rotateChange: Called whenever viewport's rotation changes. Passes the current state to the callback.
         *                     Used for:  Rotation dial, thumbnail, annotation updates
         */
    addLayer: (layer) => {
      layers.push(layer);

      if (layer.container) {
        layer.container.classList.add('us-viewport-layer');
        sizerDiv.appendChild(layer.container);
      }
    },

    /**
         * Triggers a paint event on all registered layers.
         * @param state
         * @param changes
         */
    paint (state, changes) {
      try {
        paintChildren(state, changes);
      } catch (e) {
        logger.error(e);
      }
    }
  });

  function paintChildren (state, changes) {
    // Calculate changes to the viewport's size and position
    zoom = state.zoom;
    width = viewport.getImageWidth() / zoom;
    height = viewport.getImageHeight() / zoom;
    left = -Math.floor(state.left / zoom);
    top = -Math.floor(state.top / zoom);

    // Reposition / resize the sizer div
    sizerDiv.style.left = left + 'px';
    sizerDiv.style.top = top + 'px';
    sizerDiv.style.width = width + 'px';
    sizerDiv.style.height = height + 'px';

    // Loop through and update each layer. Note: Use loop rather than .forEach for performance
    for (ix = 0; ix < layers.length; ix++) {
      layer = layers[ix];

      // Have the layers update their position data if needed.
      layer.updatePosition && layer.updatePosition({left, top, width, height, zoom});

      // Call paint for those that need it.
      layer.paint && layer.paint(state, changes);

      // Some items only care about specific changes; notify them.
      if (changes.includes('zoom')) {
        layer.zoomChange && layer.zoomChange(state);
      }
      if (changes.includes('angleDeg')) {
        layer.rotateChange && layer.rotateChange(state);
      }
    }
  }
}



// WEBPACK FOOTER //
// ./src/uScope/js/lib/services/rendering/Renderer.js